import {useMemo} from 'react';
import {useGetDocumentTreeQuery} from 'src/redux/queries/document';
import type { TreeAncestor} from 'src/shared/utils/treeUtils';
import {findAncestorsInTree} from 'src/shared/utils/treeUtils';

/**
 * Returns the ancestor chain (from root to parent) for a given document/folder node.
 * Uses the document tree API to resolve the full path.
 *
 * @param nodeId - The ID of the current node (folder or document)
 * @param parentId - The parent ID of the current node. If provided, ancestors are computed for the parent.
 *                   This is useful for files/documents where the breadcrumb should show the folder hierarchy.
 * @returns Array of ancestors from root to the parent of the current node (not including the current node itself).
 */
export const useDocumentAncestors = (
  nodeId?: string | number,
  parentId?: string | number
): TreeAncestor[] => {
  const {data: treeData} = useGetDocumentTreeQuery(
    {isLeaf: false},
    {skip: !nodeId}
  );

  return useMemo(() => {
    if (!treeData || !nodeId) return [];

    // If parentId is provided, find ancestors up to the parent (for files/documents within a folder)
    // If no parentId, find ancestors of the current node itself (for folder-list mode)
    const targetId = parentId ? String(parentId) : String(nodeId);
    const fullPath = findAncestorsInTree(targetId, treeData);

    if (parentId) {
      // For files: return the full path to the parent folder (all ancestors including the parent)
      return fullPath;
    }

    // For folders: return ancestors excluding the current node itself
    // (the current node name is shown as the last breadcrumb or as the title)
    return fullPath.slice(0, -1);
  }, [treeData, nodeId, parentId]);
};
